// src/cockpit4/components/KnowledgeFlywheelEntry.tsx
// ============================================
// 知识飞轮入口组件 - 软件操作 → 知识自动沉淀
//
// 功能：
//   1. 显示飞轮状态（累计操作/已提取卡片/闭环度）
//   2. 显示待处理操作列表
//   3. 手动触发知识提取（消耗 token，需按钮点击）
//   4. 展示最近提取的知识卡片
//   5. 决策反馈优化软件配置（反哺 Hermes，形成闭环）
//
// Token 优化：仅在用户点击"提取知识"按钮时消耗 token
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  Zap,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  RotateCw,
} from "lucide-react";
import { getKnowledgeFlywheelService } from "../../runtime/KnowledgeFlywheel/service";
import type { FlywheelState, ExtractedKnowledgeCard } from "../../runtime/KnowledgeFlywheel/service";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

export default function KnowledgeFlywheelEntry() {
  const [state, setState] = useState<FlywheelState | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    cards: ExtractedKnowledgeCard[];
    tokensUsed?: number;
    error?: string;
  } | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  // 飞轮第 5 步：已成功反哺 Hermes 配置的卡片 key 集合（用 title|domain 标识）
  const [feedbackAppliedKeys, setFeedbackAppliedKeys] = useState<Set<string>>(new Set());
  // 反馈环节执行中标记（用于按钮 loading 状态）
  const [applyingFeedback, setApplyingFeedback] = useState(false);
  // 最近一次反馈应用的错误信息（仅用于详情面板提示，不阻塞主流程）
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const loadState = useCallback(() => {
    const service = getKnowledgeFlywheelService();
    setState(service.getState());
  }, []);

  useEffect(() => {
    loadState();
    const interval = setInterval(loadState, 5000); // 5 秒刷新状态（不消耗 token）
    return () => clearInterval(interval);
  }, [loadState]);

  /**
   * 飞轮第 5 步：将提取的知识卡片反哺到 Hermes 配置，形成闭环
   *
   * 调用 RuntimeManager.execute(RuntimeName.Hermes, 'optimize_config', { cards })
   * - Hermes 不可用或 execute 失败时 console.warn 不阻塞
   * - 成功时更新 flywheelState 的 feedbackAppliedCount（通过 service.recordFeedbackApplied）
   * - 同时将本次反哺的卡片 key 加入 feedbackAppliedKeys，用于可视化标记
   */
  const applyFeedbackToConfig = useCallback(
    async (cards: ExtractedKnowledgeCard[]): Promise<void> => {
      if (!cards || cards.length === 0) return;
      setApplyingFeedback(true);
      setFeedbackError(null);
      try {
        // Hermes Runtime 不可用时仅 warn，不阻塞后续流程
        if (!RuntimeManager.hasRuntime(RuntimeName.Hermes)) {
          console.warn(
            "[KnowledgeFlywheel] RuntimeName.Hermes 未注册，跳过反馈反哺（不阻塞）"
          );
          setFeedbackError("Hermes Runtime 未注册，反馈未应用");
          return;
        }
        const resp = await RuntimeManager.execute(
          RuntimeName.Hermes,
          "optimize_config",
          { cards }
        );
        if (!resp || !resp.success) {
          console.warn(
            "[KnowledgeFlywheel] Hermes.optimize_config 执行失败，跳过反馈反哺（不阻塞）:",
            resp?.error
          );
          setFeedbackError(resp?.error || "Hermes.optimize_config 执行失败");
          return;
        }
        // 成功：更新 flywheelState.feedbackAppliedCount
        const service = getKnowledgeFlywheelService();
        service.recordFeedbackApplied(cards.length);
        // 将本次反哺的卡片 key 加入集合，用不同颜色标记
        const newKeys = new Set(feedbackAppliedKeys);
        for (const card of cards) {
          newKeys.add(`${card.title}|${card.domain}`);
        }
        setFeedbackAppliedKeys(newKeys);
        loadState();
      } catch (err: any) {
        // 任何异常都 console.warn 不阻塞
        console.warn(
          "[KnowledgeFlywheel] 反馈反哺异常，跳过（不阻塞）:",
          err?.message || err
        );
        setFeedbackError(err?.message || String(err));
      } finally {
        setApplyingFeedback(false);
      }
    },
    [feedbackAppliedKeys, loadState]
  );

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const service = getKnowledgeFlywheelService();
      const result = await service.extractKnowledge();
      setLastResult({
        success: result.success,
        cards: result.cards,
        tokensUsed: result.tokensUsed,
        error: result.error,
      });
      loadState();
      // 飞轮第 5 步：提取成功后自动将知识卡片反哺 Hermes 配置，形成闭环
      if (result.success && result.cards.length > 0) {
        // 异步触发，不阻塞提取结果展示
        void applyFeedbackToConfig(result.cards);
      }
    } catch (err: any) {
      setLastResult({
        success: false,
        cards: [],
        error: err?.message || String(err),
      });
    } finally {
      setExtracting(false);
    }
  };

  // 闭环度公式：(已提取卡片 + 反馈应用次数) / (累计操作 * 2) * 100
  // 分母乘 2：操作→卡片→反馈 共 2 个转化环节，反馈环节也纳入闭环度计算
  const closurePercent = useMemo(() => {
    if (!state) return 0;
    const totalOps = state.totalOperations || 0;
    if (totalOps === 0) return 0;
    const numerator = state.totalCards + (state.feedbackAppliedCount || 0);
    return Math.min(100, Math.round((numerator / (totalOps * 2)) * 100));
  }, [state]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.25 }}
      className="relative rounded-xl border border-violet-500/20 bg-gradient-to-br from-violet-500/[0.05] to-fuchsia-500/[0.03] overflow-hidden flex-shrink-0"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/60 to-transparent" />

      <div className="relative p-4">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
              <RefreshCw size={16} className="text-violet-400" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white">知识飞轮</h3>
              <p className="text-[10px] font-mono text-violet-500/80 uppercase tracking-wider">
                Software Operation → Knowledge Cards
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[16px] font-mono font-bold text-violet-300">{closurePercent}%</div>
              <div className="text-[9px] font-mono text-gray-600">闭环度</div>
            </div>
            <button
              onClick={handleExtract}
              disabled={extracting || (state?.pendingOperations || 0) === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-500/15 border border-violet-500/40 hover:bg-violet-500/25 transition-colors disabled:opacity-40"
              title="手动提取知识（消耗 Token）"
            >
              {extracting ? (
                <Loader2 size={12} className="text-violet-400 animate-spin" />
              ) : (
                <Zap size={12} className="text-violet-400" />
              )}
              <span className="text-[10px] font-mono text-violet-300 uppercase tracking-wider">
                {extracting ? "提取中..." : "提取知识"}
              </span>
              {state && state.pendingOperations > 0 && !extracting && (
                <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-violet-500/20 text-violet-300">
                  {state.pendingOperations}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* 状态指标 */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
            <div className="text-[9px] font-mono text-gray-500 uppercase">累计操作</div>
            <div className="text-[14px] font-mono font-bold text-gray-200">
              {state?.totalOperations || 0}
            </div>
          </div>
          <div className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
            <div className="text-[9px] font-mono text-gray-500 uppercase">已提取卡片</div>
            <div className="text-[14px] font-mono font-bold text-emerald-400">
              {state?.totalCards || 0}
            </div>
          </div>
          <div className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
            <div className="text-[9px] font-mono text-gray-500 uppercase">待处理</div>
            <div className="text-[14px] font-mono font-bold text-amber-400">
              {state?.pendingOperations || 0}
            </div>
          </div>
          {/* 飞轮第 5 步状态指示：反馈应用次数 */}
          <div className="rounded-md border border-fuchsia-500/20 bg-fuchsia-500/[0.03] px-2.5 py-1.5">
            <div className="text-[9px] font-mono text-fuchsia-400/80 uppercase flex items-center gap-0.5">
              <RotateCw size={8} />
              反馈应用
            </div>
            <div className="text-[14px] font-mono font-bold text-fuchsia-300">
              {state?.feedbackAppliedCount || 0}
            </div>
          </div>
        </div>

        {/* 闭环度进度条 */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-mono text-gray-500 uppercase">飞轮闭环度</span>
            <span className="text-[10px] font-mono text-violet-400">{closurePercent}%</span>
          </div>
          <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
              initial={{ width: 0 }}
              animate={{ width: `${closurePercent}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
          {/* 闭环度公式说明 */}
          <div className="text-[8px] font-mono text-gray-600 mt-1">
            闭环度 = (卡片 {state?.totalCards || 0} + 反馈 {state?.feedbackAppliedCount || 0}) / (操作 {(state?.totalOperations || 0) * 2}) × 100
          </div>
        </div>

        {/* 展开详情 */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-1 text-[10px] font-mono text-gray-500 hover:text-violet-400 transition-colors"
        >
          {showDetails ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          {showDetails ? "收起详情" : "查看详情"}
        </button>

        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="mt-2 pt-2 border-t border-white/[0.04] space-y-2">
                {/* 最近提取结果 */}
                {lastResult && (
                  <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                    <div className="flex items-center gap-2 mb-1.5">
                      {lastResult.success ? (
                        <CheckCircle2 size={11} className="text-emerald-400" />
                      ) : (
                        <AlertCircle size={11} className="text-red-400" />
                      )}
                      <span className="text-[10px] font-mono text-gray-400">
                        最近提取 · {lastResult.success ? `成功 ${lastResult.cards.length} 张` : "失败"}
                      </span>
                      {lastResult.tokensUsed && (
                        <span className="text-[9px] font-mono text-amber-500 ml-auto">
                          {lastResult.tokensUsed} tokens
                        </span>
                      )}
                    </div>
                    {lastResult.error && (
                      <p className="text-[10px] text-red-400">{lastResult.error}</p>
                    )}
                    {lastResult.cards.length > 0 ? (
                      <div className="space-y-1.5">
                        {lastResult.cards.slice(0, 3).map((card, i) => {
                          // 已应用反馈的卡片用不同颜色标记（飞轮第 5 步状态指示）
                          const cardKey = `${card.title}|${card.domain}`;
                          const isFeedbackApplied = feedbackAppliedKeys.has(cardKey);
                          return (
                            <div
                              key={i}
                              className={`rounded border px-2 py-1.5 ${
                                isFeedbackApplied
                                  ? "border-fuchsia-500/40 bg-fuchsia-500/[0.06]"
                                  : "border-violet-500/20 bg-violet-500/[0.03]"
                              }`}
                            >
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <BookOpen
                                  size={9}
                                  className={isFeedbackApplied ? "text-fuchsia-400" : "text-violet-400"}
                                />
                                <span className="text-[11px] text-gray-200 font-medium truncate">
                                  {card.title}
                                </span>
                                <span
                                  className={`text-[8px] font-mono px-1 py-0.5 rounded ml-auto ${
                                    isFeedbackApplied
                                      ? "bg-fuchsia-500/25 text-fuchsia-300"
                                      : "bg-violet-500/20 text-violet-300"
                                  }`}
                                >
                                  {card.domain}
                                </span>
                              </div>
                              <p className="text-[10px] text-gray-500 line-clamp-2">
                                {card.content.slice(0, 100)}...
                              </p>
                              {isFeedbackApplied && (
                                <div className="flex items-center gap-1 mt-1 text-[8px] font-mono text-fuchsia-400">
                                  <RotateCw size={8} />
                                  已反哺 Hermes 配置
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // 白皮书铁律一：无数据显示"暂无数据"
                      <div className="text-[10px] font-mono text-gray-600 text-center py-1">
                        暂无数据
                      </div>
                    )}
                    {/* 反馈环节状态指示 */}
                    {applyingFeedback && (
                      <div className="flex items-center gap-1 mt-1.5 text-[9px] font-mono text-fuchsia-400">
                        <Loader2 size={9} className="animate-spin" />
                        正在反哺 Hermes 配置...
                      </div>
                    )}
                    {feedbackError && !applyingFeedback && (
                      <div className="text-[9px] font-mono text-amber-500 mt-1.5">
                        反馈未应用：{feedbackError}
                      </div>
                    )}
                  </div>
                )}

                {/* 飞轮机制说明 */}
                <div className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <TrendingUp size={10} className="text-violet-400" />
                    <span className="text-[10px] font-mono text-gray-400 uppercase">飞轮机制</span>
                  </div>
                  <div className="text-[10px] text-gray-500 space-y-0.5">
                    <div>1. 软件操作自动记录（不消耗 Token）</div>
                    <div>2. 手动点击"提取知识"按钮，LLM 提取知识卡片</div>
                    <div>3. 知识卡片自动写入 Obsidian 09_Knowledge/Cards/</div>
                    <div>4. CEO Brain 读取知识卡片辅助决策</div>
                    {/* 第 5 步标记为已实现，显示反馈应用次数 */}
                    <div className="flex items-center gap-1 text-fuchsia-400">
                      <span>5. 决策反馈优化软件配置，形成闭环</span>
                      {(state?.feedbackAppliedCount || 0) > 0 && (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-fuchsia-500/20">
                          已应用 {state?.feedbackAppliedCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* 最近提取时间 */}
                {state?.lastExtractionAt && (
                  <div className="text-[9px] font-mono text-gray-600 text-center">
                    最近提取：{new Date(state.lastExtractionAt).toLocaleString("zh-CN")}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
